import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, ShieldCheck } from 'lucide-react';
import { api } from '../api';

const UserManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [authFilter, setAuthFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState('');

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, authFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.append('role', roleFilter);
      if (authFilter) params.append('auth_type', authFilter);
      const query = params.toString() ? `?${params.toString()}` : '';
      const data = await api.request(`/users${query}`);
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      await api.request(`/users/${userId}/approve`, { method: 'PUT' });
      fetchUsers();
    } catch (err) { alert(err.message); }
  };

  const unapproveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke access for this user?')) return;
    try {
      await api.request(`/users/${userId}/unapprove`, { method: 'PUT' });
      fetchUsers();
    } catch (err) { alert(err.message); }
  };

  const requireTermsReacceptance = async (userId) => {
    if (!window.confirm('This will require the user to re-accept Terms of Use on next login. Continue?')) return;
    try {
      await api.request(`/users/${userId}/require-terms`, { method: 'PUT' });
      alert('User will be required to re-accept Terms of Use on next login.');
      fetchUsers();
    } catch (err) { alert(err.message); }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to permanently delete user "${userName}"? This action cannot be undone.`)) return;
    try {
      await api.request(`/users/${userId}`, { method: 'DELETE' });
      alert('User deleted successfully.');
      fetchUsers();
    } catch (err) { alert(err.message); }
  };

  const updateRole = async (userId, newRole) => {
    try {
      await api.request(`/users/${userId}/role?role=${newRole}`, { method: 'PUT' });
      fetchUsers();
    } catch (err) { alert(err.message); }
  };

  const toggleSuperAdmin = async (userId, value, userName) => {
    const verb = value ? 'grant Super Admin to' : 'remove Super Admin from';
    if (!window.confirm(`Are you sure you want to ${verb} "${userName}"?`)) return;
    try {
      await api.request(`/users/${userId}/super-admin?value=${value}`, { method: 'PUT' });
      fetchUsers();
    } catch (err) { alert(err.message); }
  };

  const isCurrentUser = (userId) => currentUser && currentUser.id === userId;
  const isSuperAdmin = !!currentUser?.is_super_admin;
  // A protected row that the current (non-super) admin must NOT be able to modify
  const isProtectedFromMe = (u) => u.is_super_admin && !isSuperAdmin;

  const domainOptions = [...new Set(users.map(u => u.domain).filter(Boolean))].sort();
  const displayedUsers = domainFilter ? users.filter(u => u.domain === domainFilter) : users;
  const hasActiveFilters = !!(roleFilter || authFilter || domainFilter);
  const clearFilters = () => {
    setRoleFilter('');
    setAuthFilter('');
    setDomainFilter('');
  };

  const roleColors = {
    admin: 'bg-levelup-accent text-levelup-darker',
    content_admin: 'bg-blue-100 text-blue-700',
    training_partner: 'bg-gray-100 text-gray-700',
    ms_stakeholder: 'bg-green-100 text-green-700'
  };

  return (
    <div className="space-y-4">
      <div className="bg-levelup-dark rounded-xl p-4 text-white">
        <h1 className="text-xl font-bold">User Management</h1>
        <p className="text-gray-300 mt-1">Manage users, roles, and approvals</p>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4 border-b flex flex-wrap gap-3">
          <select
            className="input-field max-w-xs"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            data-testid="role-filter"
          >
            <option value="">All Roles</option>
            <option value="training_partner">Training Partners</option>
            <option value="content_admin">Content Admins</option>
            <option value="ms_stakeholder">Microsoft Stakeholders</option>
            <option value="admin">Admins</option>
          </select>
          <select
            className="input-field max-w-xs"
            value={authFilter}
            onChange={(e) => setAuthFilter(e.target.value)}
            data-testid="auth-type-filter"
          >
            <option value="">All Auth Sources</option>
            <option value="sso">LevelUp SSO</option>
            <option value="local">Local (Password)</option>
          </select>
          <select
            className="input-field max-w-xs"
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            data-testid="domain-filter"
          >
            <option value="">All Partner Domains</option>
            {domainOptions.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm font-medium text-levelup-dark underline hover:text-levelup-accent transition-colors"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : displayedUsers.length === 0 ? (
          <div className="p-12 text-center" data-testid="user-management-empty-state">
            <p className="text-gray-500 font-medium">No users match these filters</p>
            <p className="text-gray-400 text-sm mt-1">Try adjusting or clearing your filters below.</p>
            <button
              onClick={clearFilters}
              className="mt-4 px-4 py-2 bg-levelup-dark text-white rounded-lg text-sm font-medium hover:bg-levelup-darker transition-colors"
              data-testid="clear-filters-empty-state-btn"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">User</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Organization</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Role</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Last Login</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {displayedUsers.map(u => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-levelup-dark">{u.full_name}</p>
                          {u.is_super_admin && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-300"
                              title="Protected Super Admin — only another Super Admin can modify this account"
                              data-testid={`super-admin-badge-${u.id}`}
                            >
                              <ShieldCheck size={12} /> Super Admin
                            </span>
                          )}
                          {u.is_sso && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-levelup-accent/15 text-levelup-darker border border-levelup-accent/40"
                              title="Authenticated via LevelUp SSO"
                              data-testid={`sso-badge-${u.id}`}
                            >
                              <img src="/logo-levelup-dark.png" alt="" className="h-3" />
                              SSO
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{u.email}</p>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{u.organization}</td>
                    <td className="px-3 py-2">
                      <select
                        value={u.role}
                        onChange={(e) => updateRole(u.id, e.target.value)}
                        className={`px-2 py-1 rounded text-sm ${roleColors[u.role]} ${(isCurrentUser(u.id) || isProtectedFromMe(u)) ? 'opacity-60 cursor-not-allowed' : ''}`}
                        data-testid={`role-select-${u.id}`}
                        disabled={isCurrentUser(u.id) || isProtectedFromMe(u)}
                        title={isCurrentUser(u.id) ? "Cannot change your own role" : isProtectedFromMe(u) ? "Protected — only a Super Admin can change a Super Admin's role" : "Change user role"}
                      >
                        <option value="training_partner">Training Partner</option>
                        <option value="partner_admin">Partner Admin</option>
                        <option value="content_admin">Content Admin</option>
                        <option value="ms_stakeholder">MS Stakeholder</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {u.is_approved ? (
                        <span className="flex items-center text-green-600 text-sm">
                          <CheckCircle size={16} className="mr-1" /> Approved
                        </span>
                      ) : (
                        <span className="flex items-center text-yellow-600 text-sm">
                          <Clock size={16} className="mr-1" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                        : <span className="text-gray-400 italic">Never</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        {!u.is_approved ? (
                          <button onClick={() => approveUser(u.id)} className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600" data-testid={`approve-user-${u.id}`}>Approve</button>
                        ) : !isCurrentUser(u.id) && !isProtectedFromMe(u) && (
                          <button onClick={() => unapproveUser(u.id)} className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600" data-testid={`unapprove-user-${u.id}`}>Revoke Access</button>
                        )}
                        {!isCurrentUser(u.id) && u.is_approved && !isProtectedFromMe(u) && (
                          <button onClick={() => requireTermsReacceptance(u.id)} className="px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600" data-testid={`require-terms-${u.id}`} title="Require user to re-accept Terms of Use">Reset Terms</button>
                        )}
                        {isSuperAdmin && !isCurrentUser(u.id) && u.role === 'admin' && (
                          u.is_super_admin ? (
                            <button onClick={() => toggleSuperAdmin(u.id, false, u.full_name)} className="px-3 py-1 bg-purple-100 text-purple-700 border border-purple-300 rounded text-sm hover:bg-purple-200" data-testid={`remove-super-admin-${u.id}`} title="Remove Super Admin designation">Remove Super Admin</button>
                          ) : (
                            <button onClick={() => toggleSuperAdmin(u.id, true, u.full_name)} className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700" data-testid={`make-super-admin-${u.id}`} title="Grant protected Super Admin designation">Make Super Admin</button>
                          )
                        )}
                        {!isCurrentUser(u.id) && u.role !== 'admin' && (
                          <button onClick={() => deleteUser(u.id, u.full_name)} className="px-3 py-1 bg-gray-700 text-white rounded text-sm hover:bg-gray-800" data-testid={`delete-user-${u.id}`} title="Permanently delete this user">Delete</button>
                        )}
                        {isProtectedFromMe(u) && <span className="text-purple-400 text-sm italic flex items-center gap-1"><ShieldCheck size={13} /> Protected</span>}
                        {isCurrentUser(u.id) && <span className="text-gray-400 text-sm italic">Current User</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
